// ============================================================
// fitStore — Central Pinia store for EveFit
// Manages tabs, current fit, browser selections, toasts
// ============================================================
import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type {
  FitDetail, FitStats, ShipClass, Ship, MarketGroup, MarketItem,
  SlotType, ModuleState, SubsystemSlot,
  BrowseFit, BrowseFitsResponse,
} from '../../shared/types';
import type { Toast, ToastType } from '../composables/useApi';
import {
  fetchFit, createFit, calculateFit,
  fetchShipClasses, fetchShips,
  fetchMarketTree, fetchItemsByMarketGroup,
  searchItems as apiSearchItems,
  addModule, removeModule,
  setModuleState, setCharge,
  addSubsystem, removeSubsystem,
  addDrone, removeDrone, updateDrone,
  addCargo, removeCargo,
  addImplant, removeImplant,
  addFighter, removeFighter, updateFighter,
  browseFits, fetchFitShipClasses, fetchFitHulls,
  onToast, iconUrl, rememberFitEditToken,
} from '../composables/useApi';

// --- Tab types ---

export interface FitTab {
  id: string;
  fitID: number | null;
  fitDetail: FitDetail | null;
  fitStats: FitStats | null;
  label: string;
  loading: boolean;
  generation: number; // race condition guard
}

let tabCounter = 0;
function nextTabId(): string {
  return `tab-${++tabCounter}`;
}

export const useFitStore = defineStore('fit', () => {
  // ─── Tabs ───────────────────────────────────────────────
  const tabs = ref<FitTab[]>([]);
  const activeTabId = ref<string>('');

  const activeTab = computed<FitTab | null>(
    () => tabs.value.find(t => t.id === activeTabId.value) ?? null,
  );

  const activeFit = computed<FitDetail | null>(
    () => activeTab.value?.fitDetail ?? null,
  );

  const activeStats = computed<FitStats | null>(
    () => activeTab.value?.fitStats ?? null,
  );

  function addTab(fitID?: number, label?: string): string {
    const id = nextTabId();
    tabs.value.push({
      id,
      fitID: fitID ?? null,
      fitDetail: null,
      fitStats: null,
      label: label ?? 'New Fit',
      loading: false,
      generation: 0,
    });
    activeTabId.value = id;
    return id;
  }

  function closeTab(tabId: string): void {
    const idx = tabs.value.findIndex(t => t.id === tabId);
    if (idx < 0) return;
    tabs.value.splice(idx, 1);
    if (activeTabId.value === tabId) {
      // Switch to nearest tab
      activeTabId.value = tabs.value[Math.min(idx, tabs.value.length - 1)]?.id ?? '';
    }
  }

  function switchTab(tabId: string): void {
    activeTabId.value = tabId;
    // Switch away from browse view when user clicks a fit tab
    if (activeLeftTab.value === 'fittings') {
      activeLeftTab.value = 'ships';
    }
  }

  // ─── Skill Level Override ───────────────────────────────
  // undefined = All V (calculator default), otherwise 0-5
  const skillLevel = ref<number | undefined>(undefined);

  // ─── Fit Loading ────────────────────────────────────────

  async function loadFit(fitID: number, tabId?: string): Promise<void> {
    const tid = tabId ?? activeTabId.value;
    const tab = tabs.value.find(t => t.id === tid);
    if (!tab) {
      console.error('[fitStore] loadFit: tab not found for id', tid);
      return;
    }

    tab.loading = true;
    tab.generation++;
    const gen = tab.generation;

    try {
      const detail = await fetchFit(fitID);
      if (tab.generation !== gen) return; // stale
      tab.fitID = fitID;
      tab.fitDetail = detail;
      tab.label = detail.fitName || detail.shipName;

      // Calculate stats
      try {
        const stats = await calculateFit(fitID, skillLevel.value);
        if (tab.generation !== gen) return; // stale
        tab.fitStats = stats;
      } catch (err) {
        console.error('[fitStore] calculateFit failed:', err);
        tab.fitStats = null;
      }
    } catch (err) {
      console.error('[fitStore] loadFit failed:', err);
    } finally {
      if (tab.generation === gen) {
        tab.loading = false;
      }
    }
  }

  async function createAndLoadFit(shipTypeID: number, shipName: string): Promise<void> {
    try {
      const fitName = `${shipName} fit`;
      const result = await createFit(shipTypeID, fitName);
      const fitID = result.fitID;
      rememberFitEditToken(fitID, result.editToken);
      if (!fitID) {
        console.error('[fitStore] createFit returned no fitID:', result);
        return;
      }
      const tabId = addTab(fitID, fitName);
      await loadFit(fitID, tabId);
    } catch (err) {
      console.error('[fitStore] createAndLoadFit failed:', err);
    }
  }

  async function refreshActiveStats(): Promise<void> {
    const tab = activeTab.value;
    if (!tab?.fitID) return;

    tab.generation++;
    const gen = tab.generation;

    try {
      const stats = await calculateFit(tab.fitID, skillLevel.value);
      if (tab.generation !== gen) return;
      tab.fitStats = stats;
    } catch {
      // Silently fail - old stats remain
    }
  }

  async function refreshActiveFit(): Promise<void> {
    const tab = activeTab.value;
    if (!tab?.fitID) return;
    await loadFit(tab.fitID, tab.id);
  }

  async function setAllSkills(level: number): Promise<void> {
    // undefined means All V (calculator default)
    skillLevel.value = level === 5 ? undefined : level;
    await refreshActiveStats();
  }

  // ─── Module Operations ─────────────────────────────────

  async function addModuleToFit(typeID: number, typeName: string, slotType: SlotType, slotPosition: number): Promise<void> {
    const tab = activeTab.value;
    if (!tab?.fitID) return;
    try {
      await addModule(tab.fitID, typeID, slotType, slotPosition);
      await refreshActiveFit();
      addToast('success', `Added ${typeName}`);
    } catch {
      // Error shown by useApi
    }
  }

  async function removeModuleFromFit(moduleId: number): Promise<void> {
    const tab = activeTab.value;
    if (!tab?.fitID) return;
    try {
      await removeModule(moduleId, tab.fitID);
      await refreshActiveFit();
    } catch {
      // Error shown by useApi
    }
  }

  async function setModuleStateFit(moduleId: number, state: ModuleState): Promise<void> {
    const tab = activeTab.value;
    if (!tab?.fitID) return;
    try {
      await setModuleState(moduleId, state, tab.fitID);
      await refreshActiveFit();
    } catch {
      // Error shown by useApi
    }
  }

  async function setModuleCharge(moduleId: number, chargeTypeID: number | null): Promise<void> {
    const tab = activeTab.value;
    if (!tab?.fitID) return;
    try {
      await setCharge(moduleId, chargeTypeID, tab.fitID);
      await refreshActiveFit();
    } catch {
      // Error shown by useApi
    }
  }

  async function addSubsystemToFit(typeID: number, slot: SubsystemSlot): Promise<void> {
    const tab = activeTab.value;
    if (!tab?.fitID) return;
    try {
      await addSubsystem(tab.fitID, typeID, slot);
      await refreshActiveFit();
    } catch {
      // Error shown by useApi
    }
  }

  async function removeSubsystemFromFit(subsystemId: number): Promise<void> {
    const tab = activeTab.value;
    if (!tab?.fitID) return;
    try {
      await removeSubsystem(subsystemId, tab.fitID);
      await refreshActiveFit();
    } catch {
      // Error shown by useApi
    }
  }

  // ─── Drone Operations ────────────────────────────────────

  async function addDroneToFit(typeID: number, typeName: string, quantity: number): Promise<void> {
    const tab = activeTab.value;
    if (!tab?.fitID) return;
    try {
      await addDrone(tab.fitID, typeID, quantity);
      await refreshActiveFit();
      addToast('success', `Added ${typeName} x${quantity}`);
    } catch {
      // Error shown by useApi
    }
  }

  async function removeDroneFromFit(droneId: number): Promise<void> {
    const tab = activeTab.value;
    if (!tab?.fitID) return;
    try {
      await removeDrone(droneId, tab.fitID);
      await refreshActiveFit();
    } catch {
      // Error shown by useApi
    }
  }

  async function setDroneActiveCount(droneId: number, activeCount: number): Promise<void> {
    const tab = activeTab.value;
    if (!tab?.fitID) return;
    try {
      await updateDrone(droneId, activeCount, tab.fitID);
      await refreshActiveFit();
    } catch {
      // Error shown by useApi
    }
  }

  // ─── Cargo Operations ────────────────────────────────────

  async function addCargoToFit(typeID: number, typeName: string, quantity: number): Promise<void> {
    const tab = activeTab.value;
    if (!tab?.fitID) return;
    try {
      await addCargo(tab.fitID, typeID, quantity);
      await refreshActiveFit();
      addToast('success', `Added ${typeName} x${quantity}`);
    } catch {
      // Error shown by useApi
    }
  }

  async function removeCargoFromFit(cargoId: number): Promise<void> {
    const tab = activeTab.value;
    if (!tab?.fitID) return;
    try {
      await removeCargo(cargoId, tab.fitID);
      await refreshActiveFit();
    } catch {
      // Error shown by useApi
    }
  }

  // ─── Implant Operations ──────────────────────────────────

  async function addImplantToFit(typeID: number, typeName: string): Promise<void> {
    const tab = activeTab.value;
    if (!tab?.fitID) return;
    try {
      await addImplant(tab.fitID, typeID);
      await refreshActiveFit();
      addToast('success', `Added ${typeName}`);
    } catch {
      // Error shown by useApi
    }
  }

  async function removeImplantFromFit(implantId: number): Promise<void> {
    const tab = activeTab.value;
    if (!tab?.fitID) return;
    try {
      await removeImplant(implantId, tab.fitID);
      await refreshActiveFit();
    } catch {
      // Error shown by useApi
    }
  }

  // ─── Fighter Operations ──────────────────────────────────

  async function addFighterToFit(typeID: number, typeName: string, tubeIndex: number, squadronSize: number): Promise<void> {
    const tab = activeTab.value;
    if (!tab?.fitID) return;
    try {
      await addFighter(tab.fitID, typeID, tubeIndex, squadronSize);
      await refreshActiveFit();
      addToast('success', `Added ${typeName}`);
    } catch {
      // Error shown by useApi
    }
  }

  async function removeFighterFromFit(fighterId: number): Promise<void> {
    const tab = activeTab.value;
    if (!tab?.fitID) return;
    try {
      await removeFighter(fighterId, tab.fitID);
      await refreshActiveFit();
    } catch {
      // Error shown by useApi
    }
  }

  async function toggleFighterActive(fighterId: number, isActive: boolean): Promise<void> {
    const tab = activeTab.value;
    if (!tab?.fitID) return;
    try {
      await updateFighter(fighterId, { isActive }, tab.fitID);
      await refreshActiveFit();
    } catch {
      // Error shown by useApi
    }
  }

  // ─── Ship Browser State ─────────────────────────────────

  const selectedRace = ref<number | null>(null);
  const shipClasses = ref<ShipClass[]>([]);
  const selectedClassID = ref<number | null>(null);
  const shipList = ref<Ship[]>([]);
  const shipSearchQuery = ref('');
  const shipBrowserLoading = ref(false);

  // Excluded ship classes
  const EXCLUDED_CLASSES = ['Blueprint', 'Capsule', 'Clone'];

  async function loadShipClasses(raceID?: number | null): Promise<void> {
    selectedRace.value = raceID ?? null;
    selectedClassID.value = null;
    shipList.value = [];
    shipBrowserLoading.value = true;
    try {
      const classes = await fetchShipClasses(raceID);
      shipClasses.value = classes
        .filter(c => !EXCLUDED_CLASSES.some(ex => c.groupName.includes(ex)))
        .sort((a, b) => a.groupName.localeCompare(b.groupName));
    } catch {
      shipClasses.value = [];
    } finally {
      shipBrowserLoading.value = false;
    }
  }

  async function selectShipClass(groupID: number): Promise<void> {
    selectedClassID.value = groupID;
    shipBrowserLoading.value = true;
    try {
      shipList.value = await fetchShips(groupID, selectedRace.value);
    } catch {
      shipList.value = [];
    } finally {
      shipBrowserLoading.value = false;
    }
  }

  async function searchShips(query: string): Promise<void> {
    shipSearchQuery.value = query;
    if (query.length < 2) {
      // Reset to class-based view
      if (selectedClassID.value) {
        await selectShipClass(selectedClassID.value);
      } else {
        shipList.value = [];
      }
      return;
    }
    shipBrowserLoading.value = true;
    try {
      const items = await apiSearchItems(query, 6); // categoryID=6 is Ship
      shipList.value = items.map(i => ({
        typeID: i.typeID,
        typeName: i.typeName,
        groupID: i.groupID,
        groupName: i.groupName,
        raceID: 0,
        description: '',
        mass: 0,
        volume: 0,
      }));
    } catch {
      shipList.value = [];
    } finally {
      shipBrowserLoading.value = false;
    }
  }

  // ─── Market Browser State ───────────────────────────────

  const marketTreeRoots = ref<MarketGroup[]>([]);
  const marketTreeCache = ref<Map<number, MarketGroup[]>>(new Map());
  const expandedMarketGroups = ref<Set<number>>(new Set());
  const selectedMarketGroupID = ref<number | null>(null);
  const marketItems = ref<MarketItem[]>([]);
  const marketSearchQuery = ref('');
  const marketBrowserLoading = ref(false);

  async function loadMarketTreeRoots(): Promise<void> {
    if (marketTreeRoots.value.length > 0) return; // already loaded
    marketBrowserLoading.value = true;
    try {
      marketTreeRoots.value = await fetchMarketTree(null);
    } catch {
      marketTreeRoots.value = [];
    } finally {
      marketBrowserLoading.value = false;
    }
  }

  async function loadMarketTreeChildren(parentID: number): Promise<MarketGroup[]> {
    const cached = marketTreeCache.value.get(parentID);
    if (cached) return cached;
    const children = await fetchMarketTree(parentID);
    marketTreeCache.value.set(parentID, children);
    return children;
  }

  async function toggleMarketGroup(groupID: number): Promise<void> {
    if (expandedMarketGroups.value.has(groupID)) {
      expandedMarketGroups.value.delete(groupID);
    } else {
      expandedMarketGroups.value.add(groupID);
    }
  }

  async function selectMarketGroup(groupID: number): Promise<void> {
    selectedMarketGroupID.value = groupID;
    marketBrowserLoading.value = true;
    try {
      marketItems.value = await fetchItemsByMarketGroup(groupID);
    } catch {
      marketItems.value = [];
    } finally {
      marketBrowserLoading.value = false;
    }
  }

  async function searchMarketItems(query: string): Promise<void> {
    marketSearchQuery.value = query;
    if (query.length < 2) {
      // Reset to tree-selected view
      if (selectedMarketGroupID.value) {
        await selectMarketGroup(selectedMarketGroupID.value);
      } else {
        marketItems.value = [];
      }
      return;
    }
    marketBrowserLoading.value = true;
    try {
      const items = await apiSearchItems(query);
      // Filter to fittable categories: modules(7), charges(8), drones(18), implants(20), fighters(87)
      const fittableCategories = new Set([7, 8, 18, 20, 87]);
      marketItems.value = items
        .filter(i => fittableCategories.has(i.categoryID))
        .map(i => ({
          typeID: i.typeID,
          typeName: i.typeName,
          groupID: i.groupID,
          groupName: i.groupName,
          metaGroupID: i.metaGroupID,
          metaGroupName: i.metaGroupName,
        }));
    } catch {
      marketItems.value = [];
    } finally {
      marketBrowserLoading.value = false;
    }
  }

  // ─── Left Panel Tab ─────────────────────────────────────

  type LeftTab = 'ships' | 'market' | 'fittings' | 'personal';
  const activeLeftTab = ref<LeftTab>('ships');

  function setLeftTab(tab: LeftTab): void {
    activeLeftTab.value = tab;
    // Trigger initial loads if needed
    if (tab === 'ships' && shipClasses.value.length === 0) {
      loadShipClasses();
    }
    if (tab === 'market' && marketTreeRoots.value.length === 0) {
      loadMarketTreeRoots();
    }
  }

  // ─── Public Fits Browse ─────────────────────────────────

  const browseClassID = ref<number | null>(null);
  const browseHullTypeID = ref<number | null>(null);
  const browseSearch = ref('');
  const browseSortBy = ref('date');
  const browseSortOrder = ref('desc');
  const browseMinPrice = ref<number | null>(null);
  const browseMaxPrice = ref<number | null>(null);
  const browsePage = ref(1);
  const browseResponse = ref<BrowseFitsResponse | null>(null);
  const browseLoading = ref(false);
  const browseShipClasses = ref<ShipClass[]>([]);
  const browseHulls = ref<Ship[]>([]);

  const browseFitsList = computed(() => browseResponse.value?.fits ?? []);
  const browseTotalPages = computed(() => browseResponse.value?.totalPages ?? 0);
  const browseTotalFits = computed(() => browseResponse.value?.total ?? 0);

  async function browseInit(): Promise<void> {
    try {
      browseShipClasses.value = await fetchFitShipClasses();
    } catch { browseShipClasses.value = []; }
    await browseLoadFits();
  }

  async function browseLoadFits(): Promise<void> {
    browseLoading.value = true;
    try {
      browseResponse.value = await browseFits(
        browsePage.value,
        browseClassID.value ?? undefined,
        browseHullTypeID.value ?? undefined,
        browseSearch.value || undefined,
        browseSortBy.value,
        browseSortOrder.value,
        browseMinPrice.value ?? undefined,
        browseMaxPrice.value ?? undefined,
      );
    } catch { browseResponse.value = null; }
    finally { browseLoading.value = false; }
  }

  async function browseSetClass(classID: number | null): Promise<void> {
    browseClassID.value = classID;
    browseHullTypeID.value = null;
    browseHulls.value = [];
    browsePage.value = 1;
    if (classID) {
      try { browseHulls.value = await fetchFitHulls(classID); } catch { browseHulls.value = []; }
    }
    await browseLoadFits();
  }

  async function browseSetHull(typeID: number | null): Promise<void> {
    browseHullTypeID.value = typeID;
    browsePage.value = 1;
    await browseLoadFits();
  }

  async function browseSetSearch(q: string): Promise<void> {
    browseSearch.value = q;
    browsePage.value = 1;
    await browseLoadFits();
  }

  async function browseGoToPage(p: number): Promise<void> {
    if (p < 1 || p > browseTotalPages.value) return;
    browsePage.value = p;
    await browseLoadFits();
  }

  function browseSetSort(sortBy: string, sortOrder: string): void {
    browseSortBy.value = sortBy;
    browseSortOrder.value = sortOrder;
    browsePage.value = 1;
    browseLoadFits();
  }

  function browseSetPriceRange(min: number | null, max: number | null): void {
    browseMinPrice.value = min;
    browseMaxPrice.value = max;
    browsePage.value = 1;
    browseLoadFits();
  }

  function browseClearFilters(): void {
    browseClassID.value = null;
    browseHullTypeID.value = null;
    browseHulls.value = [];
    browseSearch.value = '';
    browseSortBy.value = 'date';
    browseSortOrder.value = 'desc';
    browseMinPrice.value = null;
    browseMaxPrice.value = null;
    browsePage.value = 1;
    browseLoadFits();
  }

  // ─── Toasts ─────────────────────────────────────────────

  const toasts = ref<Toast[]>([]);

  onToast((toast: Toast) => {
    toasts.value.push(toast);
    setTimeout(() => {
      const idx = toasts.value.findIndex(t => t.id === toast.id);
      if (idx >= 0) toasts.value.splice(idx, 1);
    }, 4000);
  });

  function addToast(type: ToastType, message: string): void {
    const toast: Toast = { id: Date.now(), type, message };
    toasts.value.push(toast);
    setTimeout(() => {
      const idx = toasts.value.findIndex(t => t.id === toast.id);
      if (idx >= 0) toasts.value.splice(idx, 1);
    }, 4000);
  }

  function dismissToast(id: number): void {
    const idx = toasts.value.findIndex(t => t.id === id);
    if (idx >= 0) toasts.value.splice(idx, 1);
  }

  // ─── Initialization ─────────────────────────────────────

  function init(): void {
    loadShipClasses();
  }

  return {
    // Tabs
    tabs,
    activeTabId,
    activeTab,
    activeFit,
    activeStats,
    addTab,
    closeTab,
    switchTab,

    // Fit operations
    loadFit,
    createAndLoadFit,
    refreshActiveStats,
    refreshActiveFit,
    setAllSkills,
    skillLevel,

    // Module operations
    addModuleToFit,
    removeModuleFromFit,
    setModuleStateFit,
    setModuleCharge,
    addSubsystemToFit,
    removeSubsystemFromFit,

    // Drone operations
    addDroneToFit,
    removeDroneFromFit,
    setDroneActiveCount,

    // Cargo operations
    addCargoToFit,
    removeCargoFromFit,

    // Implant operations
    addImplantToFit,
    removeImplantFromFit,

    // Fighter operations
    addFighterToFit,
    removeFighterFromFit,
    toggleFighterActive,

    // Ship browser
    selectedRace,
    shipClasses,
    selectedClassID,
    shipList,
    shipSearchQuery,
    shipBrowserLoading,
    loadShipClasses,
    selectShipClass,
    searchShips,

    // Market browser
    marketTreeRoots,
    marketTreeCache,
    expandedMarketGroups,
    selectedMarketGroupID,
    marketItems,
    marketSearchQuery,
    marketBrowserLoading,
    loadMarketTreeRoots,
    loadMarketTreeChildren,
    toggleMarketGroup,
    selectMarketGroup,
    searchMarketItems,

    // Public fits browse (shared between left panel filters + center panel results)
    browseClassID, browseHullTypeID, browseSearch, browsePage,
    browseSortBy, browseSortOrder, browseMinPrice, browseMaxPrice,
    browseResponse, browseLoading, browseShipClasses, browseHulls,
    browseFitsList, browseTotalPages, browseTotalFits,
    browseInit, browseLoadFits, browseSetClass, browseSetHull, browseSetSearch,
    browseSetSort, browseSetPriceRange, browseGoToPage, browseClearFilters,

    // Left panel
    activeLeftTab,
    setLeftTab,

    // Toasts
    toasts,
    addToast,
    dismissToast,

    // Init
    init,

    // Helpers
    iconUrl,
  };
});
